import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, Stepper, Step, StepLabel, Tooltip, IconButton, Modal, Button, Grid } from '@mui/material';
import { Search as SearchIcon, CheckCircle as CheckCircleIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon, ArrowForward as ArrowForwardIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { useQueryCoordinate, useMutationCoordinate } from '../../../clients/graphql/graphql.hooks';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

const GIFTS_GET_QUERY = gql`
    query giftsGet($input: GiftsGetInput!) {
        giftsGet(input: $input) {
            gifts {
                giftId
                giverUserKey
                giverEmail
                giverPhoneNumber
                giverDisplayName
                receiverUserKey
                receiverEmail
                receiverDisplayName
                receiverPhoneNumber
                amountCents
                status
                pendingPaymentAt
                claimedAt
                paidAt
                availableToClaimAt
                checkoutSessionId
                receiverHasUnclaimedGifts
                note
                createdAt
            }
            totalCount
        }
    }
`;

const JEDI_GIFT_EDIT_MUTATION = gql`
    mutation jediGiftEdit($input: JediGiftEditInput!) {
        jediGiftEdit(input: $input) {
            gift {
                giftId
                giverEmail
                giverPhoneNumber
                giverDisplayName
                receiverEmail
                receiverDisplayName
                receiverPhoneNumber
                note
                availableToClaimAt
            }
        }
    }
`;

interface Gift {
    giftId: string;
    giverUserKey?: number;
    giverEmail?: string;
    giverPhoneNumber?: string;
    giverDisplayName?: string;
    receiverUserKey?: number;
    receiverEmail?: string;
    receiverDisplayName?: string;
    receiverPhoneNumber?: string;
    amountCents?: number;
    status?: string;
    pendingPaymentAt?: string;
    claimedAt?: string;
    paidAt?: string;
    availableToClaimAt?: string;
    checkoutSessionId?: string;
    receiverHasUnclaimedGifts?: boolean;
    note?: string;
    createdAt?: string;
}

// Pipeline Progress Component
const GiftPipeline: React.FC<{ gift: Gift }> = ({ gift }) => {
    const steps = [
        { label: 'Pending', completed: !!gift.pendingPaymentAt, date: gift.pendingPaymentAt },
        { label: 'Paid', completed: !!gift.paidAt, date: gift.paidAt },
        { label: 'Claimed', completed: !!gift.claimedAt, date: gift.claimedAt }
    ];

    const activeStep = gift.claimedAt ? 3 : gift.paidAt ? 2 : gift.pendingPaymentAt ? 1 : 0;

    const getTooltipContent = () => {
        const lines = [];
        if (gift.pendingPaymentAt) {
            lines.push(`Pending: ${new Date(gift.pendingPaymentAt).toLocaleString()}`);
        }
        if (gift.paidAt) {
            lines.push(`Paid: ${new Date(gift.paidAt).toLocaleString()}`);
        }
        if (gift.claimedAt) {
            lines.push(`Claimed: ${new Date(gift.claimedAt).toLocaleString()}`);
        }
        return lines.length > 0 ? (
            <Box>
                {lines.map((line, idx) => (
                    <Typography key={idx} variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        {line}
                    </Typography>
                ))}
            </Box>
        ) : 'No dates available';
    };

    return (
        <Tooltip 
            title={getTooltipContent()} 
            arrow 
            placement="top"
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'rgba(30, 41, 59, 0.95)',
                        backdropFilter: 'blur(8px)',
                        '& .MuiTooltip-arrow': {
                            color: 'rgba(30, 41, 59, 0.95)',
                        },
                    },
                },
            }}
        >
            <Box sx={{ width: '100%', py: 0.5, cursor: 'help' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={index}>
                            {/* Step Circle */}
                            <Box sx={{ 
                                width: 16, 
                                height: 16, 
                                borderRadius: '50%',
                                background: step.completed 
                                    ? '#10b981'
                                    : '#e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: step.completed ? 'none' : '1.5px solid #cbd5e1',
                                transition: 'all 0.2s ease-in-out',
                                flexShrink: 0
                            }}>
                                {step.completed && (
                                    <Box sx={{ 
                                        width: 6, 
                                        height: 6, 
                                        borderRadius: '50%', 
                                        background: 'white' 
                                    }} />
                                )}
                            </Box>
                            
                            {/* Connector Line */}
                            {index < steps.length - 1 && (
                                <Box sx={{ 
                                    flex: 1,
                                    height: 2,
                                    background: steps[index + 1].completed ? '#10b981' : '#e2e8f0',
                                    transition: 'background 0.2s ease-in-out',
                                    mx: 0.5
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                
                {/* Labels */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    {steps.map((step, index) => (
                        <Typography 
                            key={index}
                            variant="caption" 
                            sx={{ 
                                fontSize: '0.6rem', 
                                fontWeight: step.completed ? 600 : 400,
                                color: step.completed ? '#10b981' : '#94a3b8',
                                flex: index < steps.length - 1 ? 1 : 'none',
                                textAlign: index === 0 ? 'left' : index === steps.length - 1 ? 'right' : 'center'
                            }}
                        >
                            {step.label}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Tooltip>
    );
};

export const GiftsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchEmail, setSearchEmail] = useState('');
    const [searchPhone, setSearchPhone] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        note: '',
        availableToClaimAt: '',
        giverEmail: '',
        giverDisplayName: '',
        giverPhoneNumber: '',
        receiverEmail: '',
        receiverDisplayName: '',
        receiverPhoneNumber: ''
    });

    // Build search variables to search both receiver and giver fields
    const getSearchVariables = () => {
        const baseInput: any = {
            limit: 100,
            offset: 0
        };

        // Only add filters if they have values
        if (statusFilter) {
            baseInput.status = statusFilter;
        }

        if (searchEmail) {
            // Search both receiver and giver emails
            baseInput.receiverEmail = searchEmail;
            baseInput.giverEmail = searchEmail;
        }

        if (searchPhone) {
            // Search both receiver and giver phone numbers
            baseInput.receiverPhoneNumber = searchPhone;
            baseInput.giverPhoneNumber = searchPhone;
        }

        return baseInput;
    };

    const { loading, error, data, refetch } = useQueryCoordinate(GIFTS_GET_QUERY, {
        variables: {
            input: getSearchVariables()
        },
    });

    const [editGift, { loading: editLoading }] = useMutationCoordinate(JEDI_GIFT_EDIT_MUTATION, {});

    const handleSearch = () => {
        refetch();
    };

    const handleRowClick = (gift: Gift) => {
        setSelectedGift(gift);
        setEditForm({
            note: gift.note || '',
            availableToClaimAt: gift.availableToClaimAt ? new Date(gift.availableToClaimAt).toISOString().slice(0, 16) : '',
            giverEmail: gift.giverEmail || '',
            giverDisplayName: gift.giverDisplayName || '',
            giverPhoneNumber: gift.giverPhoneNumber || '',
            receiverEmail: gift.receiverEmail || '',
            receiverDisplayName: gift.receiverDisplayName || '',
            receiverPhoneNumber: gift.receiverPhoneNumber || ''
        });
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedGift(null);
    };

    const handleSaveEdit = async () => {
        if (!selectedGift) return;

        try {
            await editGift({
                variables: {
                    input: {
                        giftId: selectedGift.giftId,
                        note: editForm.note || undefined,
                        availableToClaimAt: editForm.availableToClaimAt ? new Date(editForm.availableToClaimAt).toISOString() : undefined,
                        giverEmail: editForm.giverEmail || undefined,
                        giverDisplayName: editForm.giverDisplayName || undefined,
                        giverPhoneNumber: editForm.giverPhoneNumber || undefined,
                        receiverEmail: editForm.receiverEmail || undefined,
                        receiverDisplayName: editForm.receiverDisplayName || undefined,
                        receiverPhoneNumber: editForm.receiverPhoneNumber || undefined
                    }
                }
            });
            refetch();
            handleCloseModal();
        } catch (err) {
            console.error('Error editing gift:', err);
        }
    };

    const gifts: Gift[] = data?.giftsGet?.gifts || [];
    const totalCount = data?.giftsGet?.totalCount || 0;

    return (
        <Box sx={{ 
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            minHeight: '100vh',
            py: 4
        }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', px: 3 }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 4,
                    p: 3,
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#1e293b', mb: 1 }}>
                            Gifts
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                            Manage and view all gift transactions
                        </Typography>
                    </Box>
                    <Chip 
                        label={`${totalCount} Gifts`}
                        sx={{ 
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: 32
                        }}
                    />
                </Box>

                {/* Search Filters */}
                <Box sx={{ 
                    background: 'rgba(255,255,255,0.9)', 
                    borderRadius: 4, 
                    p: 3, 
                    mb: 4,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        SEARCH FILTERS
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Search by email (giver or receiver)"
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            size="small"
                            sx={{ 
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': {
                                    background: 'rgba(255,255,255,0.9)',
                                    fontSize: '0.875rem'
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            placeholder="Search by phone (giver or receiver)"
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            size="small"
                            sx={{ 
                                minWidth: 300,
                                '& .MuiOutlinedInput-root': {
                                    background: 'rgba(255,255,255,0.9)',
                                    fontSize: '0.875rem'
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setTimeout(handleSearch, 100);
                                }}
                                sx={{ 
                                    background: 'rgba(255,255,255,0.9)',
                                    fontSize: '0.875rem'
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="PENDING_PAYMENT">Pending Payment</MenuItem>
                                <MenuItem value="PENDING_CLAIM">Pending Claim</MenuItem>
                                <MenuItem value="CLAIMED">Claimed</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Gifts Table */}
                <Box sx={{ 
                    background: 'rgba(255,255,255,0.9)', 
                    borderRadius: 4, 
                    p: 3,
                    border: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    backdropFilter: 'blur(20px)'
                }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                            <CircularProgress size={60} sx={{ color: '#3b82f6' }} />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                            Error loading gifts. Please try again.
                        </Alert>
                    ) : gifts.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                                No gifts found. Try adjusting your search filters.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 3, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                GIFTS ({totalCount})
                            </Typography>
                            <Box sx={{ 
                                background: 'rgba(248,250,252,0.8)', 
                                borderRadius: 3, 
                                overflow: 'hidden',
                                border: '1px solid rgba(0,0,0,0.04)'
                            }}>
                                <TableContainer>
                                    <Table sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
                                        <TableHead>
                                            <TableRow sx={{ background: 'rgba(241,245,249,0.8)' }}>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Giver</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Receiver</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Amount</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2, minWidth: 180 }}>Progress</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#475569', py: 2, px: 2 }}>Note</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {gifts.map((gift: Gift, index: number) => (
                                                <TableRow 
                                                    key={gift.giftId} 
                                                    onClick={() => handleRowClick(gift)}
                                                    sx={{ 
                                                        '&:nth-of-type(even)': { background: 'rgba(255,255,255,0.4)' },
                                                        cursor: 'pointer',
                                                        '&:hover': { 
                                                            background: 'rgba(241,245,249,0.8)',
                                                            transform: 'translateY(-1px)',
                                                            transition: 'all 0.2s ease-in-out'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }} 
                                                >
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 2, px: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                                                                    {gift.giverDisplayName || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                                                                    {gift.giverEmail || gift.giverPhoneNumber || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                            {gift.giverUserKey && (
                                                                <Tooltip title="View User" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/users/${gift.giverUserKey}`);
                                                                        }}
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            color: '#3b82f6',
                                                                            '&:hover': {
                                                                                background: 'rgba(59,130,246,0.1)',
                                                                                color: '#1d4ed8'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 2, px: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                                                                    {gift.receiverDisplayName || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                                                                    {gift.receiverEmail || gift.receiverPhoneNumber || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                            {gift.receiverUserKey && (
                                                                <Tooltip title="View User" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/users/${gift.receiverUserKey}`);
                                                                        }}
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            color: '#3b82f6',
                                                                            '&:hover': {
                                                                                background: 'rgba(59,130,246,0.1)',
                                                                                color: '#1d4ed8'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 2, px: 2, fontWeight: 600 }}>
                                                        ${((gift.amountCents || 0) / 100).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2, px: 2 }}>
                                                        <Chip 
                                                            label={gift.status || 'Unknown'} 
                                                            size="small"
                                                            sx={{ 
                                                                height: 24, 
                                                                fontSize: '0.7rem',
                                                                background: gift.status === 'CLAIMED' ? '#dcfce7' : 
                                                                           gift.status === 'PENDING_CLAIM' ? '#d1fae5' : 
                                                                           gift.status === 'PENDING_PAYMENT' ? '#fef3c7' : '#f1f5f9',
                                                                color: gift.status === 'CLAIMED' ? '#166534' : 
                                                                       gift.status === 'PENDING_CLAIM' ? '#065f46' : 
                                                                       gift.status === 'PENDING_PAYMENT' ? '#92400e' : '#64748b',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2, px: 2 }}>
                                                        <GiftPipeline gift={gift} />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', py: 2, px: 2, color: '#64748b', maxWidth: 200 }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontSize: '0.75rem', 
                                                                overflow: 'hidden', 
                                                                textOverflow: 'ellipsis', 
                                                                whiteSpace: 'nowrap' 
                                                            }}
                                                        >
                                                            {gift.note || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Edit Gift Modal */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box sx={{
                    width: '90%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)'
                    }}>
                        <Box>
                            <Typography variant="h5" fontWeight={600} sx={{ color: '#1e293b', mb: 0.5 }}>
                                Edit Gift
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                Gift ID: {selectedGift?.giftId}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={{
                                color: '#64748b',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.04)',
                                    color: '#1e293b'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Modal Content */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                        {selectedGift && (
                            <Box>
                                {/* Read-only Gift Details */}
                                <Box sx={{
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: 3,
                                    p: 2.5,
                                    mb: 3,
                                    border: '1px solid rgba(0,0,0,0.04)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        GIFT DETAILS
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                Amount
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                ${((selectedGift.amountCents || 0) / 100).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                Status
                                            </Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip 
                                                    label={selectedGift.status || 'Unknown'} 
                                                    size="small"
                                                    sx={{ 
                                                        height: 24, 
                                                        fontSize: '0.7rem',
                                                        background: selectedGift.status === 'CLAIMED' ? '#dcfce7' : 
                                                                   selectedGift.status === 'PENDING_CLAIM' ? '#d1fae5' : 
                                                                   selectedGift.status === 'PENDING_PAYMENT' ? '#fef3c7' : '#f1f5f9',
                                                        color: selectedGift.status === 'CLAIMED' ? '#166534' : 
                                                               selectedGift.status === 'PENDING_CLAIM' ? '#065f46' : 
                                                               selectedGift.status === 'PENDING_PAYMENT' ? '#92400e' : '#64748b',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                Created At
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                {selectedGift.createdAt ? new Date(selectedGift.createdAt).toLocaleString() : 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                Paid At
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                {selectedGift.paidAt ? new Date(selectedGift.paidAt).toLocaleString() : 'Not paid'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                Claimed At
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                {selectedGift.claimedAt ? new Date(selectedGift.claimedAt).toLocaleString() : 'Not claimed'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Editable Fields */}
                                <Box sx={{
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: 3,
                                    p: 2.5,
                                    border: '1px solid rgba(0,0,0,0.04)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b', mb: 2, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        EDITABLE FIELDS
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Giver Display Name"
                                                value={editForm.giverDisplayName}
                                                onChange={(e) => setEditForm({ ...editForm, giverDisplayName: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Giver Email"
                                                value={editForm.giverEmail}
                                                onChange={(e) => setEditForm({ ...editForm, giverEmail: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Giver Phone Number"
                                                value={editForm.giverPhoneNumber}
                                                onChange={(e) => setEditForm({ ...editForm, giverPhoneNumber: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Receiver Display Name"
                                                value={editForm.receiverDisplayName}
                                                onChange={(e) => setEditForm({ ...editForm, receiverDisplayName: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Receiver Email"
                                                value={editForm.receiverEmail}
                                                onChange={(e) => setEditForm({ ...editForm, receiverEmail: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Receiver Phone Number"
                                                value={editForm.receiverPhoneNumber}
                                                onChange={(e) => setEditForm({ ...editForm, receiverPhoneNumber: e.target.value })}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Available to Claim At"
                                                type="datetime-local"
                                                value={editForm.availableToClaimAt}
                                                onChange={(e) => setEditForm({ ...editForm, availableToClaimAt: e.target.value })}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Note"
                                                value={editForm.note}
                                                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                                                multiline
                                                rows={3}
                                                size="small"
                                                sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.9)' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Modal Footer */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        p: 3,
                        borderTop: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)'
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#cbd5e1',
                                color: '#64748b',
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    background: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveEdit}
                            disabled={editLoading}
                            startIcon={editLoading ? <CircularProgress size={16} /> : <EditIcon />}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                }
                            }}
                        >
                            {editLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

